/**
 * AskPegasus (ASK-02) — the conversational, multi-entity analyst. You ask in
 * plain English; the `ask-pegasus` edge function (Gemini, read-only) maps the
 * question to a governed QUERY SPEC over a field catalog (the semantic layer),
 * and the frontend RUNS it — returning a NUMBER (count / sum), a per-group
 * BREAKDOWN, or a row LIST. It reaches multiple areas (customers, equipment
 * installs, invoices, dispatches), and every turn is remembered so you can
 * FOLLOW UP to refine ("what about 3 ton", "just in Eagan", "now as a total").
 *
 * Why this shape (vs a chatbot that writes SQL): the AI only fills out a spec
 * constrained to real fields — it can't invent data or run arbitrary SQL, and
 * it never states a number it didn't compute. That's the ThoughtSpot/Cortex
 * "semantic layer" pattern from research/reporting-architecture.md: accurate
 * because the model answers in the same governed vocabulary a person would.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, CornerDownLeft, AlertTriangle, ArrowRight, User } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { pagedAll } from "@/lib/pagedAll";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { useNavigateWithBack } from "@/lib/navBack";
import { displayCustomerName } from "@/lib/format";
import { applyFilters, condLabel, type ListField, type FilterCond } from "./lists/listCore";

type Row = Record<string, unknown>;
const num = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);
const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const MONEY_FIELDS = new Set(["inv_amount", "labor_cost", "mat_cost", "bill_amount"]);

// One catalog entry = a field the AI may use AND the frontend runs. col is the
// real DB column (defaults to id); value reads the fetched row for display.
function fld(id: string, label: string, type: ListField<Row>["type"] = "text", options?: string[]): ListField<Row> {
  const f: ListField<Row> = { id, label, type, col: id, filterable: true, value: (r) => (r[id] ?? null) as string | number | null };
  if (options) f.options = options.map((o) => ({ value: o, label: o }));
  return f;
}

interface EntityDef {
  key: string;
  label: string;
  noun: string;
  table: string;
  /** The unique column paging orders by (ASK-CAP-01) — every sum / group-by walks the whole set through pagedAll. */
  pk: string;
  cols: string;
  display: string[];       // default columns to SHOW for a list answer
  fields: ListField<Row>[];
  reportKey?: string;      // a list report to "open in" when one exists
}

const ENTITIES: EntityDef[] = [
  {
    key: "customers", label: "Customers", noun: "customers", table: "customer_directory", pk: "custno",
    cols: "custno,name,address,city,state,zip,phone1,email,plan_status,has_balance",
    display: ["name", "city", "plan_status", "email"],
    reportKey: "customers",
    fields: [
      { ...fld("name", "Customer"), col: "name", value: (r) => r.name as string, render: (r) => <span className="font-medium text-slate-800">{displayCustomerName(null, r.name as string) || (r.custno as string)}</span> },
      fld("address", "Address"), fld("city", "City"), fld("state", "State"), fld("zip", "Zip"),
      fld("plan_status", "Plan", "enum", ["member", "lapsed", "none"]),
      fld("email", "Email"), fld("phone1", "Phone"),
      fld("has_balance", "Owes a balance", "bool"),
    ],
  },
  {
    key: "equipment", label: "Equipment Installs", noun: "installs", table: "v_equipment_installs", pk: "counter",
    cols: "counter,custno,mfg,model,serial,eq_type,install_date,install_year,install_month,install_ym,family,tons,series",
    display: ["install_date", "model", "family", "tons", "mfg"],
    fields: [
      // NOTE: eq_type is deliberately NOT exposed — it's ~96% NULL + inconsistent
      // free-text in ESC, so the AI would grab it for "condensers/furnaces" and
      // zero the result. Equipment identity = model/family/series/tonnage (all
      // reliably derived from the model). A clean derived `category` is a future add.
      fld("model", "Model"), fld("family", "Family (e.g. EL16, ML14)"),
      fld("series", "Series", "enum", ["Elite", "Merit", "Signature", "Armstrong"]),
      fld("tons", "Tonnage", "number"), fld("mfg", "Make"),
      fld("install_year", "Install year", "number"), fld("install_month", "Install month (1-12)", "number"),
      fld("install_ym", "Install month (YYYY-MM)"), fld("install_date", "Install date", "date"),
      fld("serial", "Serial"), fld("custno", "Customer #"),
    ],
  },
  {
    key: "invoices", label: "Invoices", noun: "invoices", table: "mirror_sales", pk: "invoice",
    cols: "invoice,inv_date,ship_name,ship_csz,dept,inv_type,inv_amount,labor_cost,mat_cost,salesman,job_number,po_num",
    display: ["inv_date", "invoice", "customer", "dept", "inv_amount"],
    reportKey: "invoice-list",
    fields: [
      fld("invoice", "Invoice #"), fld("inv_date", "Invoice date", "date"),
      { ...fld("customer", "Customer"), col: "ship_name", value: (r) => r.ship_name as string },
      fld("dept", "Dept"), fld("inv_type", "Type"),
      { ...fld("inv_amount", "Amount", "number"), render: (r) => money(num(r.inv_amount)) },
      fld("labor_cost", "Labor cost", "number"), fld("mat_cost", "Material cost", "number"),
      fld("salesman", "Salesperson"),
      { ...fld("city", "City / State / Zip"), col: "ship_csz", value: (r) => r.ship_csz as string },
      fld("job_number", "Job #"), fld("po_num", "PO #"),
    ],
  },
  {
    key: "dispatches", label: "Dispatches", noun: "dispatches", table: "v_dispatch_full", pk: "dispatch",
    cols: "dispatch,appointment_date,customer_fullname,city,tech_name,status,tech_dept",
    display: ["appointment_date", "customer", "city", "tech", "status"],
    reportKey: "dispatch-list",
    fields: [
      fld("appointment_date", "Date", "date"),
      { ...fld("customer", "Customer"), col: "customer_fullname", value: (r) => r.customer_fullname as string },
      fld("city", "City"),
      { ...fld("tech", "Tech"), col: "tech_name", value: (r) => r.tech_name as string },
      fld("status", "Status"),
      { ...fld("dept", "Dept"), col: "tech_dept", value: (r) => r.tech_dept as string },
    ],
  },
];
const entityByKey = (k: string) => ENTITIES.find((e) => e.key === k) ?? ENTITIES[0];

interface Spec {
  entity: string;
  filters: { field: string; op: string; value: string; value2?: string }[];
  group: string | null;
  measure: { fn: "count" | "sum" | "list"; field?: string } | null;
  columns: string[];
  sort: { field: string; dir: "asc" | "desc" } | null;
  explanation: string;
  error?: string;
}

type Result =
  | { kind: "error"; message: string }
  | { kind: "number"; total: number; money: boolean; label: string; groups?: { key: string; v: number }[]; groupLabel?: string; capped?: boolean }
  | { kind: "list"; rows: Row[]; count: number; columns: string[] };

interface Turn { question: string; spec?: Spec; result?: Result; pending: boolean }

// ASK-CAP-01 (review 2026-11-02 P0 #4): PostgREST returns at most 1,000 rows per request whatever
// .limit() says, so a sum or group-by over more than that was silently short and the old 10,000
// "capped" flag could never fire. Aggregates now page the whole set (pagedAll, ordered by the
// entity's pk); CAP is the honest ceiling the answer admits to when it is hit.
const CAP = 100000;
const LIST_CAP = 500;

const EXAMPLES = [
  "How many EL16 condensers did we install last June, by tonnage?",
  "Total invoiced last month by department",
  "Members in Eagan who owe a balance",
  "How many dispatches did each tech run in the last 30 days?",
];

export function AskPegasus() {
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigateWithBack();
  const endRef = useRef<HTMLDivElement>(null);

  const catalogs = useMemo(
    () => ENTITIES.map((e) => ({ entity: e.key, label: e.label, fields: e.fields.map((f) => ({ id: f.id, label: f.label, type: f.type ?? "text", options: f.options?.map((o) => o.value) })) })),
    [],
  );

  // Follow the conversation as it grows — never on arrival: with no turns yet
  // this jumped the whole Reports page down to the empty Ask box (34gd).
  useEffect(() => {
    if (turns.length) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [turns]);

  async function runSpec(spec: Spec): Promise<Result> {
    if (spec.error) return { kind: "error", message: spec.error };
    const ent = entityByKey(spec.entity);
    const conds: FilterCond[] = spec.filters.map((f, i) => ({ id: String(i), field: f.field, op: f.op, value: f.value, value2: f.value2 }));
    const fields = ent.fields as ListField<unknown>[];
    const measure = spec.measure ?? { fn: "list" as const };
    const groupField = spec.group ? ent.fields.find((f) => f.id === spec.group) : null;
    const groupCol = groupField ? (groupField.col ?? groupField.id) : null;

    try {
      if (measure.fn === "list") {
        let query = supabase.from(ent.table).select(ent.cols, { count: "exact" });
        query = applyFilters(query, conds, fields);
        if (spec.sort) { const sf = ent.fields.find((f) => f.id === spec.sort!.field); if (sf) query = query.order(sf.col ?? sf.id, { ascending: spec.sort!.dir === "asc" }); }
        const { data, count, error } = await query.limit(LIST_CAP);
        if (error) throw error;
        const columns = (spec.columns.length ? spec.columns : ent.display).filter((id) => ent.fields.some((f) => f.id === id));
        return { kind: "list", rows: (data ?? []) as unknown as Row[], count: count ?? 0, columns: columns.length ? columns : ent.display };
      }

      if (measure.fn === "count" && !groupCol) {
        let query = supabase.from(ent.table).select("*", { count: "exact", head: true });
        query = applyFilters(query, conds, fields);
        const { count, error } = await query;
        if (error) throw error;
        return { kind: "number", total: count ?? 0, money: false, label: ent.noun };
      }

      // Grouped count, or any sum: fetch the needed columns + aggregate client-side.
      const sumCol = measure.fn === "sum" ? (ent.fields.find((f) => f.id === measure.field)?.col ?? measure.field ?? null) : null;
      const sel = [ent.pk, groupCol, sumCol].filter(Boolean).filter((c, i, a) => a.indexOf(c) === i).join(",");
      const rows = await pagedAll<Row>(() => {
        let q = supabase.from(ent.table).select(sel);
        q = applyFilters(q, conds, fields);
        return q.order(ent.pk, { ascending: true });
      }, CAP);
      const isMoney = measure.fn === "sum" && !!sumCol && MONEY_FIELDS.has(sumCol);
      const valOf = (r: Row) => (measure.fn === "sum" && sumCol ? num(r[sumCol]) : 1);

      if (groupCol) {
        const m = new Map<string, number>();
        for (const r of rows) {
          const raw = r[groupCol];
          const k = raw == null || raw === "" ? "—" : (groupField?.type === "number" ? String(parseFloat(String(raw))) : String(raw));
          m.set(k, (m.get(k) ?? 0) + valOf(r));
        }
        const groups = [...m.entries()].map(([key, v]) => ({ key, v })).sort((a, b) => b.v - a.v);
        const total = groups.reduce((s, g) => s + g.v, 0);
        return { kind: "number", total, money: isMoney, label: ent.noun, groups, groupLabel: groupField?.label ?? spec.group ?? "", capped: rows.length >= CAP };
      }
      const total = rows.reduce((s, r) => s + valOf(r), 0);
      return { kind: "number", total, money: isMoney, label: ent.noun, capped: rows.length >= CAP };
    } catch (e) {
      return { kind: "error", message: (e as Error)?.message ?? "Something went wrong running that." };
    }
  }

  async function ask(question: string) {
    const text = question.trim();
    if (!text || busy) return;
    setQ("");
    setBusy(true);
    const idx = turns.length;
    setTurns((t) => [...t, { question: text, pending: true }]);
    const history = turns.filter((t) => t.spec && t.result?.kind !== "error").slice(-6).map((t) => ({ question: t.question, spec: t.spec }));
    try {
      const { data, error } = await supabase.functions.invoke("ask-pegasus", { body: { question: text, catalogs, history } });
      if (error) throw error;
      const spec = data as Spec;
      const result = await runSpec(spec);
      setTurns((t) => t.map((x, i) => (i === idx ? { ...x, spec, result, pending: false } : x)));
    } catch {
      setTurns((t) => t.map((x, i) => (i === idx ? { ...x, result: { kind: "error", message: "Couldn't reach Ask Pegasus — try again." }, pending: false } : x)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-3 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/70 via-white to-white p-4">
        <div className="flex items-center gap-2 text-sm font-bold text-violet-700">
          <Sparkles className="h-4 w-4" /> Ask Pegasus
          <InfoTip label="How Ask Pegasus works">Ask about customers, equipment installs, invoices or dispatches to get a number, a breakdown or a list, then ask a follow-up to narrow it. It uses only your real data and can't make up numbers; if an answer looks off, rephrase the question.</InfoTip>
        </div>
      </div>

      <div className="space-y-4">
        {turns.length === 0 && (
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex) => (
              <button key={ex} onClick={() => ask(ex)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-violet-300 hover:text-violet-700">{ex}</button>
            ))}
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className="space-y-2">
            <div className="flex justify-end">
              <div className="inline-flex items-start gap-2 rounded-2xl rounded-tr-sm bg-violet-600 px-3.5 py-2 text-sm text-white shadow-sm">
                <span>{t.question}</span>
                <User className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" />
              </div>
            </div>
            <AnswerCard turn={t} onRefine={(rk) => navigate(`/reports?report=${rk}`)} />
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-400/20">
        <Sparkles className="h-4 w-4 shrink-0 text-violet-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={turns.length ? "Ask a follow-up…" : "e.g. how many EL16 2-ton did we install this year?"} className="w-full bg-transparent text-sm text-slate-800 focus:outline-none" />
        <button type="submit" disabled={busy || !q.trim()} className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:bg-violet-300">
          {busy ? "Thinking…" : <>Ask <CornerDownLeft className="h-3.5 w-3.5" /></>}
        </button>
      </form>
    </div>
  );
}

function AnswerCard({ turn, onRefine }: { turn: Turn; onRefine: (reportKey: string) => void }) {
  if (turn.pending) {
    return <div className="rounded-2xl rounded-tl-sm border border-hairline bg-white p-3.5 text-sm text-slate-400 shadow-e1">Pegasus is working on it…</div>;
  }
  const { spec, result } = turn;
  if (!result || !spec) return null;
  const ent = entityByKey(spec.entity);

  if (result.kind === "error") {
    return (
      <div className="flex items-start gap-2 rounded-2xl rounded-tl-sm border border-amber-200 bg-amber-50/60 p-3.5 text-sm text-amber-800 shadow-e1">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {result.message}
      </div>
    );
  }

  const conds: FilterCond[] = spec.filters.map((f, i) => ({ id: String(i), field: f.field, op: f.op, value: f.value, value2: f.value2 }));

  return (
    <div className="rounded-2xl rounded-tl-sm border border-hairline bg-white p-3.5 shadow-e1">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Here's how I read that</div>
      <p className="mt-0.5 text-sm text-slate-700">{spec.explanation}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{ent.label}</span>
        {conds.map((c) => <span key={c.id} className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">{condLabel(c, ent.fields as ListField<unknown>[])}</span>)}
        {result.kind === "number" && result.groupLabel && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">by {result.groupLabel}</span>}
      </div>

      <div className="mt-3">
        {result.kind === "number" ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums text-slate-900">{result.money ? money(result.total) : result.total.toLocaleString()}</span>
              {!result.money && <span className="text-sm text-slate-500">{result.label}</span>}
            </div>
            {result.groups && result.groups.length > 0 && (
              <div className="mt-2 overflow-hidden rounded-lg border border-slate-100">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50/70 text-left text-[11px] uppercase tracking-wider text-slate-500"><th className="px-3 py-1.5">{result.groupLabel || "Group"}</th><th className="px-3 py-1.5 text-right">{result.money ? "Total" : "Count"}</th></tr></thead>
                  <tbody>
                    {result.groups.map((g) => (
                      <tr key={g.key} className="border-t border-slate-50">
                        <td className="px-3 py-1.5 text-slate-700">{g.key}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-800">{result.money ? money(g.v) : g.v.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {result.capped && <p className="mt-1 text-[11px] text-amber-600">Computed over the first {CAP.toLocaleString()} matching rows — narrow the question for an exact total.</p>}
          </>
        ) : (
          <ListAnswer ent={ent} rows={result.rows} count={result.count} columns={result.columns} />
        )}
      </div>

      {ent.reportKey && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-50 pt-2.5">
          <button onClick={() => onRefine(ent.reportKey!)} className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline">Open in the {ent.label} report <ArrowRight className="h-3 w-3" /></button>
        </div>
      )}
    </div>
  );
}

function ListAnswer({ ent, rows, count, columns }: { ent: EntityDef; rows: Row[]; count: number; columns: string[] }) {
  const cols = columns.map((id) => ent.fields.find((f) => f.id === id)).filter(Boolean) as ListField<Row>[];
  return (
    <div>
      <div className="mb-1.5 text-sm text-slate-600"><span className="font-bold text-slate-900">{count.toLocaleString()}</span> {ent.noun}{count > rows.length ? ` · showing ${rows.length}` : ""}</div>
      <div className="max-h-80 overflow-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50/90"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">{cols.map((f) => <th key={f.id} className={cn("px-3 py-1.5", f.type === "number" && "text-right")}>{f.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-50 hover:bg-violet-50/30">
                {cols.map((f) => <td key={f.id} className={cn("px-3 py-1.5 text-slate-700", f.type === "number" && "text-right tabular-nums")}>{f.render ? f.render(r) : (f.value(r) ?? <span className="text-slate-300">—</span>)}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-sm text-slate-400">No matches.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
